import React, { useEffect, useMemo, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "@/components/icons";
import { toast } from "sonner";
import { plural } from "@/lib/plural";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { useBalk } from "@/lib/balkState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { MoneyInput } from "@/components/MoneyInput";
import { parseAmount, amountToInput } from "@/lib/money";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const NONE = "__none__";

const empty = {
  asset_code: "",
  name: "",
  category_id: "",
  location_id: NONE,
  ruimte_id: NONE,
  spoor: NONE,
  entity: NONE,
  brand: "",
  model: "",
  serial_number: "",
  purchase_date: "",
  purchase_price_ex_vat: "",
  warranty_until: "",
  expected_lifetime_months: "",
  expected_lifetime_usage: "",
  usage_counter: "",
  replacement_date_override: "",
  responsibility: "ons",
  status: "actief",
  notes: "",
  supplier: { name: "", contact: "", phone: "", email: "" },
};

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const strOrNull = (v) => (v === "" || v === null || v === undefined ? null : v);

export default function AssetForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { refresh: refreshBalk } = useBalk();
  const [form, setForm] = useState(empty);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [ruimtes, setRuimtes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/asset-categories"),
      api.get("/locations"),
      api.get("/ruimtes"),
      api.get("/entities"),
    ])
      .then(([c, l, r, e]) => {
        setCategories(c.data);
        setLocations(l.data);
        setRuimtes(r.data);
        setEntities(e.data);
      })
      .catch((err) => toast.error(getErrorMessage(err, "Kon referentiedata niet laden.")));

    if (isEdit) {
      (async () => {
        try {
          const { data } = await api.get(`/assets/${id}`);
          setForm({
            ...empty,
            ...data,
            location_id: data.location_id || NONE,
            ruimte_id: data.ruimte_id || NONE,
            spoor: data.spoor || NONE,
            name: data.name || "",
            entity: data.entity || NONE,
            purchase_price_ex_vat: amountToInput(data.purchase_price_ex_vat),
            expected_lifetime_months: data.expected_lifetime_months ?? "",
            expected_lifetime_usage: data.expected_lifetime_usage ?? "",
            usage_counter: data.usage_counter ?? "",
            purchase_date: data.purchase_date || "",
            warranty_until: data.warranty_until || "",
            replacement_date_override: data.replacement_date_override || "",
            supplier: data.supplier || empty.supplier,
          });
        } catch (e) {
          setError(getErrorMessage(e, "Asset niet gevonden."));
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSupplier = (k, v) => setForm((f) => ({ ...f, supplier: { ...f.supplier, [k]: v } }));

  const activeCategories = categories.filter((c) => c.active || c.category_id === form.category_id);
  const locationRuimtes = useMemo(
    () => ruimtes.filter((r) => r.location_id === form.location_id),
    [ruimtes, form.location_id]
  );
  const currentRuimte = useMemo(
    () => ruimtes.find((r) => r.id === form.ruimte_id),
    [ruimtes, form.ruimte_id]
  );
  // A spoor only exists inside a was-hal, so the field only opens there.
  const sporen = currentRuimte?.kind === "washal" ? (currentRuimte.sporen || []) : [];

  const onLocationChange = (v) => {
    setForm((f) => ({ ...f, location_id: v, ruimte_id: NONE, spoor: NONE }));
  };
  const onRuimteChange = (v) => {
    setForm((f) => ({ ...f, ruimte_id: v, spoor: NONE }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.category_id) return setError("Categorie is verplicht.");

    const payload = {
      asset_code: strOrNull(form.asset_code?.trim()),
      category_id: form.category_id,
      name: form.name,
      location_id: form.location_id === NONE ? null : form.location_id,
      ruimte_id: form.ruimte_id === NONE ? null : form.ruimte_id,
      spoor: form.spoor === NONE ? null : form.spoor,
      entity: form.entity === NONE ? null : form.entity,
      brand: form.brand,
      model: form.model,
      serial_number: form.serial_number,
      purchase_date: strOrNull(form.purchase_date),
      purchase_price_ex_vat: parseAmount(form.purchase_price_ex_vat).value,
      warranty_until: strOrNull(form.warranty_until),
      expected_lifetime_months: numOrNull(form.expected_lifetime_months),
      expected_lifetime_usage: numOrNull(form.expected_lifetime_usage),
      usage_counter: numOrNull(form.usage_counter),
      replacement_date_override: strOrNull(form.replacement_date_override),
      responsibility: form.responsibility,
      status: form.status,
      notes: form.notes,
      supplier: form.supplier,
    };

    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/assets/${id}`, payload);
        toast.success("Asset bijgewerkt");
        // §2.2: a corrected debiteur link must reach the werkbonnen that use
        // it, instead of moving them silently.
        const mee = data?.debiteur_meegegaan || [];
        if (mee.length) {
          toast.success(`${plural(mee.length, "werkbon gaat", "werkbonnen gaan")} mee naar ${mee[0].naar_naam || "geen debiteur"}`);
        }
        const opConcept = data?.debiteur_op_concept || [];
        if (opConcept.length) {
          toast.warning(`${plural(opConcept.length, "werkbon staat", "werkbonnen staan")} op een concept en gaat niet mee`);
        }
        // De balk lights on the machine's own status and its spoor, so a saved
        // asset refreshes the strip like every other write path does.
        refreshBalk();
        navigate(`/assets/${id}`);
      } else {
        const { data } = await api.post("/assets", payload);
        toast.success(`Asset aangemaakt (${data.asset_code})`);
        refreshBalk();
        navigate(`/assets/${data.id}`);
      }
    } catch (err) {
      setError(getErrorMessage(err, "Opslaan mislukt."));
    } finally {
      setSaving(false);
    }
  };

  // A loading state is still a screen: it keeps the console, so navigation and
  // the gutter do not disappear for a second and come back.
  if (loading)
    return (
      <Page console={{ variant: "view", title: isEdit ? "Asset bewerken" : "Nieuw asset" }}>
        <div className="max-w-4xl space-y-4" data-testid="asset-form-loading">
          <Card className="p-5"><Skeleton className="h-40 w-full" /></Card>
        </div>
      </Page>
    );

  return (
    <Page console={{ variant: "view", title: isEdit ? "Asset bewerken" : "Nieuw asset",
      facts: [isEdit ? "wijzigingen gelden direct" : "code en QR worden automatisch gemaakt"],
      actions: [{ key: "terug", label: "Terug", icon: ArrowLeft, onClick: () => navigate(-1) }] }}>
      <div className="max-w-4xl space-y-4">

      <form onSubmit={onSubmit} className="space-y-4">
        <Card className="p-5">
          <h2 className="mb-4 text-body font-semibold">Algemeen</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="naam">Naam</Label>
              {/* What the machine is called on the floor: "Machine 1",
                  "Slangen baan 2". The code identifies it, the name is what a
                  TD man says out loud. */}
              <Input id="naam" data-testid="asset-name-input" value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Bijv. Machine 1" />
            </div>
            <div className="space-y-1.5">
              <Label>Categorie *</Label>
              <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                <SelectTrigger data-testid="asset-category-select"><SelectValue placeholder="Kies categorie" /></SelectTrigger>
                <SelectContent>
                  {activeCategories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="code">Assetcode</Label>
              <Input id="code" data-testid="asset-code-input" className="font-mono" value={form.asset_code || ""} onChange={(e) => set("asset_code", e.target.value)} placeholder="Automatisch als leeg (bijv. TW-ROO-WAS-01)" />
            </div>
            <div className="space-y-1.5">
              <Label>Locatie</Label>
              <Select value={form.location_id} onValueChange={onLocationChange}>
                <SelectTrigger data-testid="asset-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Geen (bijv. voertuig)</SelectItem>
                  {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              {/* Owner's wording: a question, not a noun. */}
              <Label>Waar staat het?</Label>
              <Select value={form.ruimte_id} onValueChange={onRuimteChange} disabled={form.location_id === NONE || locationRuimtes.length === 0}>
                <SelectTrigger data-testid="asset-ruimte-select">
                  <SelectValue placeholder={locationRuimtes.length ? "Kies ruimte" : "Deze locatie heeft nog geen ruimtes"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Nog niet ingedeeld</SelectItem>
                  {locationRuimtes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Spoor</Label>
              <Select value={form.spoor} onValueChange={(v) => set("spoor", v)} disabled={sporen.length === 0}>
                <SelectTrigger data-testid="asset-spoor-select">
                  <SelectValue placeholder={sporen.length ? "Kies spoor" : "Alleen in een was-hal"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Geen</SelectItem>
                  {sporen.map((sp) => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Entiteit</Label>
              <Select value={form.entity} onValueChange={(v) => set("entity", v)}>
                <SelectTrigger data-testid="asset-entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Geen</SelectItem>
                  {entities.map((en) => <SelectItem key={en.entity_id} value={en.entity_id}>{en.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Verantwoordelijkheid</Label>
              <Select value={form.responsibility} onValueChange={(v) => set("responsibility", v)}>
                <SelectTrigger data-testid="asset-responsibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ons">Ons</SelectItem>
                  <SelectItem value="verhuurder">Verhuurder</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="asset-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actief">Actief</SelectItem>
                  <SelectItem value="defect">Defect</SelectItem>
                  <SelectItem value="in_reparatie">In reparatie</SelectItem>
                  <SelectItem value="vervangen">Vervangen</SelectItem>
                  <SelectItem value="afgevoerd">Afgevoerd</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label htmlFor="brand">Merk</Label><Input id="brand" data-testid="asset-brand-input" value={form.brand} onChange={(e) => set("brand", e.target.value)} /></div>
            <div className="space-y-1.5"><Label htmlFor="model">Model</Label><Input id="model" data-testid="asset-model-input" value={form.model} onChange={(e) => set("model", e.target.value)} /></div>
            <div className="space-y-1.5"><Label htmlFor="serial">Serienummer</Label><Input id="serial" value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} /></div>
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="mb-1 text-body font-semibold">Aanschaf &amp; levensduur</h2>
          <p className="mb-4 text-data text-muted-foreground">Alles mag leeg blijven; onvolledige assets verschijnen onder het filter "Aanvullen".</p>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="pd">Aanschafdatum</Label><Input id="pd" type="date" data-testid="asset-purchase-date-input" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label htmlFor="pp">Aanschafprijs excl. btw</Label><MoneyInput id="pp" testid="asset-price-input" value={form.purchase_price_ex_vat} onChange={(v) => set("purchase_price_ex_vat", v)} /></div>
            <div className="space-y-1.5"><Label htmlFor="wu">Garantie tot</Label><Input id="wu" type="date" value={form.warranty_until} onChange={(e) => set("warranty_until", e.target.value)} /></div>
            <div className="space-y-1.5"><Label htmlFor="elm">Verwachte levensduur (maanden, afwijkend)</Label><Input id="elm" type="number" data-testid="asset-lifetime-months-input" value={form.expected_lifetime_months} onChange={(e) => set("expected_lifetime_months", e.target.value)} placeholder="Leeg = standaard/locatie" /></div>
            <div className="space-y-1.5"><Label htmlFor="elu">Verwachte levensduur (gebruik)</Label><Input id="elu" type="number" value={form.expected_lifetime_usage} onChange={(e) => set("expected_lifetime_usage", e.target.value)} /></div>
            <div className="space-y-1.5"><Label htmlFor="uc">Gebruiksteller</Label><Input id="uc" type="number" value={form.usage_counter} onChange={(e) => set("usage_counter", e.target.value)} /></div>
            <div className="space-y-1.5"><Label htmlFor="rdo">Vervangdatum (handmatig)</Label><Input id="rdo" type="date" value={form.replacement_date_override} onChange={(e) => set("replacement_date_override", e.target.value)} /></div>
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-body font-semibold">Leverancier</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-1.5"><Label>Naam</Label><Input value={form.supplier.name} onChange={(e) => setSupplier("name", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Contactpersoon</Label><Input value={form.supplier.contact} onChange={(e) => setSupplier("contact", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Telefoon</Label><Input value={form.supplier.phone} onChange={(e) => setSupplier("phone", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>E-mail</Label><Input value={form.supplier.email} onChange={(e) => setSupplier("email", e.target.value)} /></div>
            <div className="space-y-1.5 lg:col-span-2"><Label>Notities</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
        </Card>

        {error && <p className="text-body text-destructive">{error}</p>}
        <div className="flex items-center gap-2">
          <Button type="submit" data-testid="asset-form-save-button" disabled={saving}>{saving ? "Bezig..." : "Opslaan"}</Button>
          <Button type="button" variant="secondary" onClick={() => navigate(-1)}>Annuleren</Button>
        </div>
      </form>
      </div>
    </Page>
  );
}
